import { withSentry } from "@sentry/remix";
import { Outlet } from "react-router";
import { Document } from "./components/Document";
import type { LoaderFunctionArgs, LinksFunction } from "react-router";

import "./tailwind.css";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import { ROLES } from "~/utils/roles";
import { getMsSession } from "~/utils/ms-auth.server";
import { isFeatureEnabled } from "~/models/ms-feature-flags.server";
import {
  MEDIASCRIBE_HOSTING_FLAG,
  SETTINGS_MENU_VOCABULARIES_FLAG,
} from "~/utils/feature-flag-keys";
import type { Account } from "~/components/AccountOrgSelector";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Gelasio:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Lexend:wght@100..900&family=Roboto:wght@400;500;700&display=swap",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  // Get the multi-account session (handles legacy session conversion)
  const msSession = await getMsSession(request);

  // Derive user info from the active account in msSession
  const activeAccount = msSession?.accounts.find(
    (a) => a.id.toString() === msSession.activeAccountId?.toString(),
  );
  const activeOrgRole = activeAccount?.roles.find(
    (r) => r.organization_id?.toString() === msSession?.activeOrgId?.toString(),
  );

  const isAdmin =
    activeAccount?.isSystemAdmin ||
    activeAccount?.isSupportStaff ||
    activeAccount?.isEngineer ||
    false;
  const accountSettingsRoles: string[] = [
    ROLES.SYSTEM_ADMIN,
    ROLES.ADMIN,
    ROLES.ACCOUNT_OWNER,
  ];
  const hasAccountSettingsAccess = activeAccount
    ? msSession?.activeOrgId != null &&
      (activeAccount.isSystemAdmin ||
        (activeAccount.roles ?? []).some((r) =>
          accountSettingsRoles.includes(r.role),
        ))
    : false;

  // The "Sites" (MediaScribe Hosting) surface is doubly gated: the org must have
  // the mediascribe_hosting feature flag on, AND the user must be an org admin/
  // owner (the same trio as Account Settings). Only controls MENU VISIBILITY —
  // the routes re-check both server-side via requireSitesAccess.
  const hasMediascribeHosting = hasAccountSettingsAccess
    ? await isFeatureEnabled(
        MEDIASCRIBE_HOSTING_FLAG,
        msSession?.activeOrgId ?? undefined,
        {
          isSystemAdmin: activeAccount?.isSystemAdmin,
          isSupportStaff: activeAccount?.isSupportStaff,
          isEngineer: activeAccount?.isEngineer,
        },
      )
    : false;
  const canManageSites = hasAccountSettingsAccess && hasMediascribeHosting;

  // Vocabularies has no role gating — any member of an organization may manage its
  // vocabularies, so the only base requirement is an active org to scope them to.
  // The settings_menu_vocabularies flag controls VISIBILITY only (this menu entry and
  // the New Project vocabulary picker), same as mediascribe_hosting above —
  // /vocabularies itself stays reachable and governed by requireVocabulariesAccess
  // regardless of this flag.
  const hasVocabulariesSettingsMenuFlag = msSession?.activeOrgId
    ? await isFeatureEnabled(
        SETTINGS_MENU_VOCABULARIES_FLAG,
        msSession.activeOrgId,
        {
          isSystemAdmin: activeAccount?.isSystemAdmin,
          isSupportStaff: activeAccount?.isSupportStaff,
          isEngineer: activeAccount?.isEngineer,
        },
      )
    : false;
  const canManageVocabularies =
    msSession?.activeOrgId != null && hasVocabulariesSettingsMenuFlag;

  // Build accounts array for the AccountOrgSelector
  const accounts: Account[] = msSession
    ? msSession.accounts.map((account) => ({
        id: account.id.toString(),
        email: account.email,
        firstName: account.firstName,
        lastName: account.lastName,
        isActive:
          account.id.toString() === msSession.activeAccountId?.toString(),
        organizations: (account.roles ?? [])
          .filter((r) => r.organization_id != null)
          .map((r) => ({
            id: r.organization_id!.toString(),
            name: r.org_name,
            role: r.role,
            isActive:
              account.id.toString() === msSession.activeAccountId.toString() &&
              r.organization_id?.toString() ===
                msSession.activeOrgId?.toString(),
          })),
      }))
    : [];

  return {
    currentOrganizationId: msSession?.activeOrgId?.toString(),
    currentOrganizationName: activeOrgRole?.org_name,
    currentRole: activeOrgRole?.role,
    isAdmin,
    isSystemAdmin: activeAccount?.isSystemAdmin ?? false,
    isSupportStaff: activeAccount?.isSupportStaff ?? false,
    isEngineer: activeAccount?.isEngineer ?? false,
    hasAccountSettingsAccess,
    canManageSites,
    canManageVocabularies,
    accounts,
  };
}

function App() {
  return (
    <Document>
      <Outlet />
    </Document>
  );
}

export { ErrorBoundary } from "./components/ErrorBoundary";
export default withSentry(App);
